import React, { useState, useRef, useCallback, useEffect } from 'react';

import {
	AlertCircle,
	Building2,
	Clock,
	Code,
	Copy,
	Check,
	DollarSign,
	FileText,
	Gauge,
	Globe,
	Headphones,
	Key,
	List,
	Lock,
	RefreshCw,
	ScrollText,
	Settings,
	Upload,
	Users,
	Zap,
} from 'lucide-react';

import {
	Badge,
	Card,
	CardBody,
	CardHeader,
	SearchInput,
	Typography,
} from '../components/common';
import Logo from '../components/Logo';

// ─── Password Gate ────────────────────────────────────────────────────────────

const STORAGE_KEY = 'api_docs_auth';
const CORRECT_PASSWORD = 'readthedocs';

const PasswordGate: React.FC<{ onAuthenticated: () => void }> = ({
	onAuthenticated,
}) => {
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');

	const handleSubmit = useCallback(
		(e: React.FormEvent) => {
			e.preventDefault();
			if (password === CORRECT_PASSWORD) {
				sessionStorage.setItem(STORAGE_KEY, 'true');
				onAuthenticated();
				setError('');
			} else {
				setError('Incorrect password. Please try again.');
			}
		},
		[password, onAuthenticated],
	);

	return (
		<div className="flex items-center justify-center min-h-screen bg-gray-50">
			<div className="bg-white rounded-xl p-10 shadow-lg max-w-sm w-full text-center">
				<div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mx-auto mb-6">
					<Lock className="w-6 h-6 text-white" />
				</div>
				<h1 className="text-xl font-bold text-gray-900 mb-2">
					API Documentation
				</h1>
				<p className="text-sm text-gray-500 mb-6">
					This document is password protected. Please enter the password to
					continue.
				</p>
				<form onSubmit={handleSubmit}>
					<input
						type="password"
						value={password}
						onChange={(e) => {
							setPassword(e.target.value);
							setError('');
						}}
						placeholder="Enter password"
						autoFocus
						className={`w-full px-4 py-3 border rounded-lg text-sm outline-none mb-3 transition-colors focus:border-purple-500 ${
							error ? 'border-red-500' : 'border-gray-300'
						}`}
					/>
					{error && <p className="text-sm text-red-600 mb-3">{error}</p>}
					<button
						type="submit"
						className="w-full py-3 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
					>
						Access Documentation
					</button>
				</form>
			</div>
		</div>
	);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CodeBlock: React.FC<{
	children: string;
	language?: string;
	className?: string;
}> = ({ children, className = '' }) => {
	const [copied, setCopied] = useState(false);
	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(children);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [children]);

	return (
		<div className={`relative group ${className}`}>
			<button
				onClick={handleCopy}
				className="absolute top-2 right-2 p-1.5 rounded-md bg-gray-700 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-600"
				title="Copy to clipboard"
			>
				{copied ? (
					<Check className="w-3.5 h-3.5" />
				) : (
					<Copy className="w-3.5 h-3.5" />
				)}
			</button>
			<pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs font-mono overflow-x-auto leading-relaxed">
				{children}
			</pre>
		</div>
	);
};

const ParamTable: React.FC<{
	params: {
		name: string;
		type: string;
		required?: boolean;
		description: string;
	}[];
}> = ({ params }) => (
	<div className="overflow-x-auto">
		<table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
			<thead className="bg-gray-50">
				<tr>
					<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
						Parameter
					</th>
					<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
						Type
					</th>
					<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
						Description
					</th>
				</tr>
			</thead>
			<tbody>
				{params.map((p) => (
					<tr key={p.name} className="border-b border-gray-100 last:border-0">
						<td className="px-3 py-2">
							<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
								{p.name}
							</code>
							{p.required && (
								<span className="ml-1 text-red-500 text-xs">*</span>
							)}
						</td>
						<td className="px-3 py-2 text-gray-600 text-xs">{p.type}</td>
						<td className="px-3 py-2 text-gray-700">{p.description}</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

const MethodBadge: React.FC<{ method: string }> = ({ method }) => {
	const colorMap: Record<string, string> = {
		GET: 'bg-green-100 text-green-800 border-green-200',
		POST: 'bg-blue-100 text-blue-800 border-blue-200',
		PUT: 'bg-yellow-100 text-yellow-800 border-yellow-200',
		PATCH: 'bg-orange-100 text-orange-800 border-orange-200',
		DELETE: 'bg-red-100 text-red-800 border-red-200',
	};
	return (
		<span
			className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${colorMap[method] || 'bg-gray-100 text-gray-800'}`}
		>
			{method}
		</span>
	);
};

const Endpoint: React.FC<{ method: string; path: string }> = ({
	method,
	path,
}) => (
	<div className="flex items-center gap-2 mb-3">
		<MethodBadge method={method} />
		<code className="text-sm font-mono font-medium text-gray-800">{path}</code>
	</div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ApiDocsPage: React.FC = () => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [activeSection, setActiveSection] = useState('quick-start');
	const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

	useEffect(() => {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (stored === 'true') {
			setIsAuthenticated(true);
		}
	}, []);

	// ─── Active section tracking (IntersectionObserver) ──────────────────
	useEffect(() => {
		if (!isAuthenticated) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
				if (visible.length > 0 && visible[0].target.id) {
					setActiveSection(visible[0].target.id);
				}
			},
			{ rootMargin: '-80px 0px -60% 0px', threshold: 0 },
		);

		// Observe after a short delay so refs are populated
		const timer = setTimeout(() => {
			Object.entries(sectionRefs.current).forEach(([, el]) => {
				if (el) observer.observe(el);
			});
		}, 200);

		return () => {
			clearTimeout(timer);
			observer.disconnect();
		};
	}, [isAuthenticated, searchQuery]);

	const scrollToSection = useCallback((id: string) => {
		sectionRefs.current[id]?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	}, []);

	if (!isAuthenticated) {
		return <PasswordGate onAuthenticated={() => setIsAuthenticated(true)} />;
	}

	// ─── Section Definitions ────────────────────────────────────────────

	const sections = [
		{
			id: 'authentication',
			title: 'Authentication',
			icon: Key,
			content: (
				<div className="space-y-4">
					<Typography variant="body1" className="mb-4">
						The API uses API keys to authenticate requests. You can view and
						manage your API keys under Settings {'>'}{' '}
						API Keys.
					</Typography>
					<Typography variant="body2" className="text-gray-600 mb-4">
						Your API keys carry significant privileges, so keep them secure.
						Don&apos;t share them in publicly accessible areas like GitHub or
						client-side code.
					</Typography>
					<div className="space-y-3">
						<Typography variant="body2" weight="medium">
							Required Headers:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'X-API-Key',
									type: 'String',
									required: true,
									description:
										'Your secret API key. Starts with mb_live_ for production or mb_test_ for sandbox.',
								},
								{
									name: 'X-Tenant-ID',
									type: 'UUID',
									required: false,
									description:
										"Your organization's unique identifier. Optional if using an Organization-scoped API key.",
								},
							]}
						/>
					</div>
					<CodeBlock>{`curl https://app.mightybot.ai/statement-analyzer/api/statement/files \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid"`}</CodeBlock>
					<Typography variant="body2" color="muted">
						<strong>Base URL:</strong>{' '}
						https://app.mightybot.ai/statement-analyzer/api
					</Typography>
					<Typography variant="body2" className="text-gray-500 text-xs">
						All API requests must be made over HTTPS. Calls made over plain HTTP
						will fail.
					</Typography>
				</div>
			),
		},
		{
			id: 'complete-example',
			title: 'Complete Example',
			icon: Code,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" color="muted" className="mb-4">
						Three API calls. That&apos;s it. Upload, poll, and retrieve your
						savings analysis.
					</Typography>

					<div className="space-y-1">
						<Typography variant="body2" weight="medium">
							1. Upload statement
						</Typography>
						<CodeBlock>{`curl -X POST https://app.mightybot.ai/statement-analyzer/api/statement/upload \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid" \\
  -F "file=@statement.pdf" \\
  -F "statement_type=vendor_statement" \\
  -F "analysis_type=cost_comparison" \\
  -F "business_type=restaurants" \\
  -F "rate_plan_type=IC_PLUS"`}</CodeBlock>
					</div>

					<div className="space-y-1">
						<Typography variant="body2" weight="medium">
							Response:
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "message": "File uploaded successfully and analysis started",
  "file_id": "550e8400-e29b-41d4-a716-446655440000",
  "filename": "statement.pdf",
  "task_id": "660e8400-e29b-41d4-a716-446655440000",
  "status": "processing"
}`}</CodeBlock>
					</div>

					<div className="space-y-1">
						<Typography variant="body2" weight="medium">
							2. Check status (poll every 5-10 seconds)
						</Typography>
						<CodeBlock>{`curl https://app.mightybot.ai/statement-analyzer/api/statement/status/550e8400-e29b-41d4-a716-446655440000 \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid"`}</CodeBlock>
					</div>

					<div className="space-y-1">
						<Typography variant="body2" weight="medium">
							3. Get results
						</Typography>
						<CodeBlock>{`curl https://app.mightybot.ai/statement-analyzer/api/statement/document/550e8400-e29b-41d4-a716-446655440000/detailed \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid"`}</CodeBlock>
					</div>
				</div>
			),
		},
		{
			id: 'statement-upload',
			title: 'Statement Upload',
			icon: Upload,
			content: (
				<div className="space-y-4">
					<Endpoint method="POST" path="/api/statement/upload" />
					<Typography variant="body2" color="muted" className="mb-4">
						Upload a merchant processing statement PDF for analysis.
					</Typography>

					<Typography variant="body2" weight="medium" className="text-gray-700">
						Request Format: multipart/form-data
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Required Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'file',
									type: 'File',
									required: true,
									description: 'Statement PDF file (max 50MB)',
								},
								{
									name: 'statement_type',
									type: 'String',
									required: true,
									description:
										'vendor_statement, fee_schedule, interchange_report, or processor_statement',
								},
								{
									name: 'analysis_type',
									type: 'String',
									required: true,
									description:
										'cost_comparison (recommended), fee_schedule_analysis, ic_plus_comparison, or interchange_analysis',
								},
								{
									name: 'business_type',
									type: 'String',
									required: true,
									description:
										'Business category (e.g., restaurants, retail, ecommerce, card_not_present)',
								},
								{
									name: 'rate_plan_type',
									type: 'String',
									required: true,
									description: 'IC_PLUS, TIERED, or FLAT_TIERED',
								},
							]}
						/>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Optional Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'priority',
									type: 'Integer',
									description: 'Processing priority 1-10 (default: 5)',
								},
								{
									name: 'custom_rates_id',
									type: 'UUID',
									description: 'Use stored custom rates configuration',
								},
								{
									name: 'skip_cache',
									type: 'Boolean',
									description:
										'Force full processing without cache (default: false)',
								},
							]}
						/>
					</div>

					<Typography variant="body2" className="text-gray-500 text-xs">
						<strong>Limits:</strong> Max file size: 50MB. Supported formats: PDF
						(for IC_PLUS and TIERED), Excel/CSV (for FLAT_TIERED only).
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Example Request:
						</Typography>
						<CodeBlock>{`curl -X POST https://app.mightybot.ai/statement-analyzer/api/statement/upload \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid" \\
  -F "file=@statement.pdf" \\
  -F "statement_type=vendor_statement" \\
  -F "analysis_type=cost_comparison" \\
  -F "business_type=restaurants" \\
  -F "rate_plan_type=IC_PLUS"`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (201 Created):
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "message": "File uploaded successfully and analysis started",
  "file_id": "550e8400-e29b-41d4-a716-446655440000",
  "filename": "statement.pdf",
  "s3_key": "dev/files/550e8400-..._statement.pdf",
  "file_size": 245678,
  "statement_type": "vendor_statement",
  "analysis_type": "cost_comparison",
  "business_type": "restaurants",
  "task_id": "660e8400-e29b-41d4-a716-446655440000",
  "status": "processing",
  "timestamp": "2024-01-27T10:30:00Z"
}`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Error Responses:
						</Typography>
						<ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">400</code>{' '}
								Bad Request: Invalid file type, missing required parameters
							</li>
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">402</code>{' '}
								Payment Required: Insufficient credits
							</li>
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">413</code>{' '}
								Payload Too Large: File exceeds 50MB
							</li>
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">500</code>{' '}
								Internal Server Error: Upload failure
							</li>
						</ul>
					</div>
				</div>
			),
		},
		{
			id: 'statement-status',
			title: 'Check Status',
			icon: Clock,
			content: (
				<div className="space-y-4">
					<Endpoint method="GET" path="/api/statement/status/{file_id}" />
					<Typography variant="body2" color="muted" className="mb-4">
						Check processing status for a statement. Poll every 5-10 seconds
						until status is <code>completed</code> or <code>failed</code>.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Path Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'file_id',
									type: 'UUID',
									required: true,
									description: 'Statement file ID from upload response',
								},
							]}
						/>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Status Values:
						</Typography>
						<div className="flex flex-wrap gap-2">
							<Badge variant="gray">uploaded</Badge>
							<Badge variant="warning">processing</Badge>
							<Badge variant="success">completed</Badge>
							<Badge variant="danger">failed</Badge>
							<Badge variant="gray">cancelled</Badge>
						</div>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (200 OK) - Completed:
						</Typography>
						<CodeBlock>{`{
  "status": "completed",
  "logs": [
    {
      "id": "990e8400-e29b-41d4-a716-446655440000",
      "status": "completed",
      "step_name": "Statement Received",
      "message": "We've securely received your statement and started the analysis",
      "created_at": "2024-01-27T10:30:00Z"
    },
    {
      "id": "aa0e8400-e29b-41d4-a716-446655440000",
      "status": "completed",
      "step_name": "Cost Analysis",
      "message": "Analysis completed successfully",
      "created_at": "2024-01-27T10:35:00Z"
    }
  ]
}`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response - Failed:
						</Typography>
						<CodeBlock>{`{
  "status": "failed",
  "logs": [
    {
      "id": "bb0e8400-e29b-41d4-a716-446655440000",
      "status": "failed",
      "step_name": "Extraction",
      "message": "Unable to extract text from PDF. File may be corrupted or password-protected.",
      "created_at": "2024-01-27T10:32:00Z"
    }
  ]
}`}</CodeBlock>
					</div>
				</div>
			),
		},
		{
			id: 'statement-results',
			title: 'Get Results',
			icon: FileText,
			content: (
				<div className="space-y-4">
					<Endpoint
						method="GET"
						path="/api/statement/document/{file_id}/detailed"
					/>
					<Typography variant="body2" color="muted" className="mb-4">
						Retrieve complete analysis results for a processed statement.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Path Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'file_id',
									type: 'UUID',
									required: true,
									description: 'Statement file ID',
								},
							]}
						/>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (200 OK):
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "file_id": "550e8400-e29b-41d4-a716-446655440000",
  "filename": "statement.pdf",
  "status": "completed",
  "processing_results": {
    "analysis_type": "cost_comparison",
    "comparison_data": {
      "total_volume": 125000.50,
      "total_transactions": 4523,
      "total_fees": 3456.78,
      "effective_rate": 0.02765,
      "processors": [
        {
          "name": "Current Processor",
          "fees": 3456.78,
          "rate": 0.02765,
          "volume": 125000.50,
          "transaction_count": 4523
        },
        {
          "name": "Alternative Processor",
          "fees": 2567.34,
          "rate": 0.02054,
          "volume": 125000.50,
          "transaction_count": 4523
        }
      ],
      "savings": {
        "amount": 889.44,
        "percentage": 25.73
      },
      "breakdown": {
        "visa_credit": {
          "volume": 65000.00,
          "transactions": 2100,
          "fees": 1650.00,
          "rate": 0.02538
        },
        "mastercard_credit": {
          "volume": 45000.00,
          "transactions": 1800,
          "fees": 1215.00,
          "rate": 0.02700
        },
        "amex": {
          "volume": 15000.50,
          "transactions": 623,
          "fees": 591.78,
          "rate": 0.03945
        }
      },
      "fee_analysis": {
        "interchange_fees": 2100.50,
        "assessment_fees": 156.28,
        "processor_markup": 1200.00
      }
    },
    "processing_metadata": {
      "config_name": "Default Rates",
      "rates_source": "defaults",
      "custom_rates_id": null
    }
  },
  "signed_urls": {
    "original_file": "https://s3.amazonaws.com/bucket/path?signature=...",
    "line_items": "https://s3.amazonaws.com/bucket/path?signature=..."
  }
}`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Error Responses:
						</Typography>
						<ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-2">
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">400</code>{' '}
								FILE_NOT_READY: Results only available when status is completed
							</li>
							<li>
								<code className="bg-gray-100 px-1 rounded text-xs">404</code>{' '}
								FILE_NOT_FOUND: Statement file not found
							</li>
						</ul>
					</div>
				</div>
			),
		},
		{
			id: 'statement-list',
			title: 'Statement History',
			icon: List,
			content: (
				<div className="space-y-4">
					<Endpoint method="GET" path="/api/statement/files" />
					<Typography variant="body2" color="muted" className="mb-4">
						List all statements for your tenant.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Query Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'page',
									type: 'Integer',
									description: 'Page number (default: 1)',
								},
								{
									name: 'page_size',
									type: 'Integer',
									description: 'Items per page (default: 50, max: 100)',
								},
								{
									name: 'search',
									type: 'String',
									description: 'Search by filename',
								},
								{
									name: 'uploaded_by',
									type: 'String',
									description: 'Filter by uploader email (comma-separated)',
								},
								{
									name: 'profit_status',
									type: 'String',
									description: 'Filter by profit status: positive or negative',
								},
							]}
						/>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Example Request:
						</Typography>
						<CodeBlock>{`curl "https://app.mightybot.ai/statement-analyzer/api/statement/files?page=1&page_size=10" \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid"`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (200 OK):
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "files": [
    {
      "id": "550e8400-e29b-41d4-a716-446655440000",
      "filename": "statement_sep_2024.pdf",
      "original_filename": "Statement_September_2024.pdf",
      "file_size": 245678,
      "statement_type": "vendor_statement",
      "analysis_type": "cost_comparison",
      "rate_plan_type": "IC_PLUS",
      "processing_status": "completed",
      "created_at": "2024-09-15T10:30:00Z",
      "updated_at": "2024-09-15T10:35:00Z",
      "processed_at": "2024-09-15T10:35:00Z",
      "uploaded_by": {
        "name": "John Doe",
        "email": "john@example.com"
      }
    }
  ],
  "total": 42,
  "page": 1,
  "page_size": 50,
  "total_pages": 1
}`}</CodeBlock>
					</div>
				</div>
			),
		},
		{
			id: 'recalculate',
			title: 'Recalculate',
			icon: RefreshCw,
			content: (
				<div className="space-y-4">
					<Endpoint
						method="POST"
						path="/api/statement/files/{file_id}/recalculate"
					/>
					<Typography variant="body2" color="muted" className="mb-4">
						Recalculate statement costs with a new rates configuration. Use
						either a stored configuration ID or provide rates directly.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Request Parameters:
						</Typography>
						<ParamTable
							params={[
								{
									name: 'rerun',
									type: 'Boolean',
									description:
										'If true, rerun full analysis. If false, use existing extraction (default: false)',
								},
								{
									name: 'custom_rates_id',
									type: 'UUID',
									description:
										'ID of stored custom rates config (required if direct_rates not provided)',
								},
								{
									name: 'direct_rates',
									type: 'Object',
									description:
										'Direct rates configuration (required if custom_rates_id not provided)',
								},
							]}
						/>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Example - Using stored config:
						</Typography>
						<CodeBlock>{`curl -X POST https://app.mightybot.ai/statement-analyzer/api/statement/files/550e8400-.../recalculate \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid" \\
  -H "Content-Type: application/json" \\
  -d '{
    "rerun": false,
    "custom_rates_id": "bb0e8400-e29b-41d4-a716-446655440000"
  }'`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Example - Using direct rates:
						</Typography>
						<CodeBlock>{`{
  "rerun": false,
  "direct_rates": {
    "rate_plan_type": "IC_PLUS",
    "pricing_mode": {
      "discount_rate_mode": "same_for_all",
      "authorization_fee_mode": "same_for_all",
      "pass_through_cost_mode": "standard"
    },
    "rates": {
      "standard_rates": {
        "mode": "same_for_all",
        "same_for_all": {
          "discount_rate": 0.0165,
          "authorization_fee": 0.10
        }
      }
    },
    "pass_through_costs": {
      "use_custom_assessment_rates": false,
      "use_bundled_amex_rate": false
    }
  }
}`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (200 OK):
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "message": "Enhanced recalculation started successfully",
  "file_id": "550e8400-e29b-41d4-a716-446655440000",
  "task_id": "110e8400-e29b-41d4-a716-446655440000",
  "rates_source": "stored_configuration",
  "rates_config_id": "bb0e8400-e29b-41d4-a716-446655440000",
  "rates_config_name": "Custom Rates Config A"
}`}</CodeBlock>
					</div>

					<Typography variant="body2" className="text-gray-500 text-xs">
						After recalculation, poll the status endpoint until completed, then
						retrieve updated results.
					</Typography>
				</div>
			),
		},
		{
			id: 'processing-logs',
			title: 'Processing Logs',
			icon: ScrollText,
			content: (
				<div className="space-y-4">
					<Endpoint
						method="GET"
						path="/api/statement/document/{file_id}/logs"
					/>
					<Typography variant="body2" color="muted" className="mb-4">
						Get all processing logs for a statement file.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Response (200 OK):
						</Typography>
						<CodeBlock>{`{
  "success": true,
  "file_id": "550e8400-e29b-41d4-a716-446655440000",
  "logs": [
    {
      "id": "990e8400-e29b-41d4-a716-446655440000",
      "status": "completed",
      "step_name": "Statement Received",
      "message": "We've securely received your statement and started the analysis",
      "details": null,
      "created_at": "2024-01-27T10:30:00Z"
    },
    {
      "id": "aa0e8400-e29b-41d4-a716-446655440000",
      "status": "processing",
      "step_name": "Recalculation",
      "message": "Recalculating costs with new rates",
      "details": {
        "rerun": false,
        "config_name": "Custom Rates Config A",
        "rates_source": "stored_configuration"
      },
      "created_at": "2024-01-27T10:32:00Z"
    }
  ],
  "total": 2
}`}</CodeBlock>
					</div>
				</div>
			),
		},
		{
			id: 'custom-rates',
			title: 'Custom Rates Configuration',
			icon: Settings,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" className="mb-4">
						Manage custom rate configurations that can be reused across multiple
						statement analyses.
					</Typography>

					<div className="space-y-4">
						{/* POST */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="POST" path="/api/statement/custom-rates" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-3"
							>
								Create a new custom rates configuration.
							</Typography>
							<CodeBlock>{`{
  "name": "Q1 2024 Rates",
  "description": "Custom rates for first quarter",
  "is_default": false,
  "rate_plan_type": "IC_PLUS",
  "custom_rates": {
    "Visa": {
      "discount_rate": 0.0165,
      "authorization_fee": 0.10
    },
    "Mastercard": {
      "discount_rate": 0.0170,
      "authorization_fee": 0.10
    },
    "American Express": {
      "tiered_interchange_rate": 0.0250,
      "assessments": 0.0015
    }
  }
}`}</CodeBlock>
						</div>

						{/* GET list */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="GET" path="/api/statement/custom-rates" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-2"
							>
								List all custom rates configurations. Supports <code>page</code>
								, <code>page_size</code>, <code>search</code>, and{' '}
								<code>is_default</code> query parameters.
							</Typography>
						</div>

						{/* GET by ID */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="GET"
								path="/api/statement/custom-rates/{rates_id}"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Get a specific custom rates configuration by ID.
							</Typography>
						</div>

						{/* GET default */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="GET"
								path="/api/statement/custom-rates/default"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Get the default custom rates configuration for your tenant.
							</Typography>
						</div>

						{/* PUT */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="PUT"
								path="/api/statement/custom-rates/{rates_id}"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Update an existing custom rates configuration. All fields are
								optional (partial update).
							</Typography>
						</div>

						{/* DELETE */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="DELETE"
								path="/api/statement/custom-rates/{rates_id}"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Delete a custom rates configuration.
							</Typography>
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'revenue-configs',
			title: 'Revenue Configuration',
			icon: DollarSign,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" className="mb-4">
						Manage revenue configurations for risk tier-based pricing. Must
						provide exactly 3 tiers: LOW, MEDIUM, and HIGH.
					</Typography>

					<div className="space-y-4">
						{/* POST */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="POST" path="/api/revenue-configs" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-3"
							>
								Create a new revenue configuration with risk tier-based pricing.
							</Typography>
							<CodeBlock>{`{
  "name": "Schedule A",
  "description": "Schedule A for ISO",
  "is_active": true,
  "configurations": [
    {
      "risk_tier": "LOW",
      "discount_buy_rate": 0.0125,
      "authorization_buy_rate": 0.03,
      "discount_buy_rate_vmd": 0.0125,
      "discount_buy_rate_amex": 0.0150,
      "auth_buy_rate_standard": 0.03,
      "auth_buy_rate_pin_debit": 0.05,
      "auth_buy_rate_ebt": 0.10,
      "batch_fee_rate": 0.25,
      "monthly_fee_costs": [
        {"label": "PCI Fee", "amount": 4.95},
        {"label": "Gateway Fee", "amount": 15.00}
      ],
      "agent_revenue_share_percent": 80.00
    },
    { "risk_tier": "MEDIUM", "..." : "..." },
    { "risk_tier": "HIGH", "..." : "..." }
  ]
}`}</CodeBlock>
						</div>

						{/* GET list */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="GET" path="/api/revenue-configs" />
							<Typography variant="body2" color="muted" className="text-sm">
								List all revenue configurations for your tenant.
							</Typography>
						</div>

						{/* GET by ID */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="GET" path="/api/revenue-configs/{config_id}" />
							<Typography variant="body2" color="muted" className="text-sm">
								Get a specific revenue configuration by ID. Returns array of
								tier configurations (LOW, MEDIUM, HIGH).
							</Typography>
						</div>

						{/* GET active by tier */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="GET"
								path="/api/revenue-configs/active/{risk_tier}"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Get the active revenue configuration for a specific risk tier
								(LOW, MEDIUM, or HIGH).
							</Typography>
						</div>

						{/* PUT */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="PUT" path="/api/revenue-configs/{config_id}" />
							<Typography variant="body2" color="muted" className="text-sm">
								Update an existing revenue configuration.
							</Typography>
						</div>

						{/* DELETE */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint
								method="DELETE"
								path="/api/revenue-configs/{config_id}"
							/>
							<Typography variant="body2" color="muted" className="text-sm">
								Delete a revenue configuration (all tiers).
							</Typography>
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'enterprise-settings',
			title: 'Organization Settings',
			icon: Building2,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" className="mb-4">
						Manage organization branding, contact information, and company
						details.
					</Typography>

					<div className="space-y-4">
						{/* GET */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="GET" path="/api/enterprise-settings" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-3"
							>
								Get organization settings for your tenant.
							</Typography>
							<CodeBlock>{`{
  "id": "ff0e8400-e29b-41d4-a716-446655440000",
  "enterprise_id": "770e8400-e29b-41d4-a716-446655440000",
  "company_name": "Acme Corporation",
  "logo_base64": "data:image/png;base64,...",
  "address": {
    "street": "123 Main St",
    "city": "San Francisco",
    "state": "CA",
    "zip": "94105",
    "country": "USA"
  },
  "contact": {
    "phone": "+1-555-123-4567",
    "email": "contact@acme.com",
    "website": "https://acme.com"
  }
}`}</CodeBlock>
						</div>

						{/* POST */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="POST" path="/api/enterprise-settings" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-3"
							>
								Create organization settings with optional logo upload. Requires{' '}
								<code>admin</code> or <code>enterprise_admin</code> role.
							</Typography>
							<Typography variant="body2" weight="medium" className="mb-2">
								Parameters (multipart/form-data):
							</Typography>
							<ParamTable
								params={[
									{
										name: 'company_name',
										type: 'String',
										required: true,
										description: 'Company name',
									},
									{
										name: 'logo',
										type: 'File',
										description:
											'Logo file (PNG, JPG, JPEG, GIF, SVG, max 2MB)',
									},
									{
										name: 'address_street',
										type: 'String',
										description: 'Street address',
									},
									{
										name: 'address_city',
										type: 'String',
										description: 'City',
									},
									{
										name: 'address_state',
										type: 'String',
										description: 'State',
									},
									{
										name: 'address_zip',
										type: 'String',
										description: 'ZIP code',
									},
									{
										name: 'contact_phone',
										type: 'String',
										description: 'Phone number',
									},
									{
										name: 'contact_email',
										type: 'String',
										description: 'Contact email',
									},
									{
										name: 'contact_website',
										type: 'String',
										description: 'Website URL',
									},
								]}
							/>
						</div>

						{/* PATCH */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="PATCH" path="/api/enterprise-settings" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-2"
							>
								Update organization settings (partial update supported). Requires{' '}
								<code>admin</code> or <code>enterprise_admin</code> role. Same
								parameters as create - all optional.
							</Typography>
							<CodeBlock>{`curl -X PATCH https://app.mightybot.ai/statement-analyzer/api/enterprise-settings \\
  -H "X-API-Key: mb_live_xxxxxxxxxxxxxx" \\
  -H "X-Tenant-ID: your-tenant-uuid" \\
  -F "company_name=Updated Company Name" \\
  -F "contact_email=newemail@acme.com" \\
  -F "logo=@/path/to/logo.png"`}</CodeBlock>
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'tenant-management',
			title: 'Tenant Management',
			icon: Users,
			content: (
				<div className="space-y-4">
					<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
						<Typography variant="body2" className="text-yellow-800">
							<strong>Admin Only:</strong> These endpoints require{' '}
							<code>super_admin</code> role and are used for platform
							administration.
						</Typography>
					</div>

					<div className="space-y-4">
						{/* POST */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="POST" path="/api/admin/tenants" />
							<Typography
								variant="body2"
								color="muted"
								className="text-sm mb-3"
							>
								Create a new tenant with its own isolated schema.
							</Typography>
							<Typography variant="body2" weight="medium" className="mb-2">
								Required Parameters:
							</Typography>
							<ParamTable
								params={[
									{
										name: 'name',
										type: 'String',
										required: true,
										description: 'Tenant name (1-255 characters)',
									},
									{
										name: 'subdomain',
										type: 'String',
										required: true,
										description:
											"Subdomain (e.g., 'acme' for acme.mightybot.ai)",
									},
									{
										name: 'company_email',
										type: 'String',
										required: true,
										description: 'Company email address',
									},
								]}
							/>
							<Typography variant="body2" weight="medium" className="mt-3 mb-2">
								Optional Parameters:
							</Typography>
							<ParamTable
								params={[
									{
										name: 'plan',
										type: 'String',
										description:
											'basic, professional, or enterprise (default: basic)',
									},
									{
										name: 'status',
										type: 'String',
										description:
											'provisioning, active, suspended, or deleted (default: provisioning)',
									},
									{
										name: 'max_enterprises',
										type: 'Integer',
										description: 'Maximum number of organizations (default: 5)',
									},
									{
										name: 'max_users_total',
										type: 'Integer',
										description: 'Maximum total users (default: 50)',
									},
									{
										name: 'max_storage_gb',
										type: 'Integer',
										description: 'Maximum storage in GB (default: 100)',
									},
									{
										name: 'billing_email',
										type: 'String',
										description: 'Billing email address',
									},
									{
										name: 'custom_domain',
										type: 'String',
										description: "Custom domain (e.g., 'app.acme.com')",
									},
									{
										name: 'metadata',
										type: 'Object',
										description: 'Additional metadata as JSON object',
									},
								]}
							/>
						</div>

						{/* GET list */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="GET" path="/api/admin/tenants" />
							<Typography variant="body2" color="muted" className="text-sm">
								List all tenants. Use <code>active_only=true</code> query
								parameter to filter active tenants only.
							</Typography>
						</div>

						{/* PUT */}
						<div className="border border-gray-200 rounded-lg p-4">
							<Endpoint method="PUT" path="/api/admin/tenants/{tenant_id}" />
							<Typography variant="body2" color="muted" className="text-sm">
								Update an existing tenant. All fields are optional (partial
								update).
							</Typography>
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'errors',
			title: 'Errors',
			icon: AlertCircle,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" className="mb-4">
						The API uses standard HTTP status codes to indicate success or
						failure.
					</Typography>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							HTTP Status Codes:
						</Typography>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
							<div className="flex items-center gap-2">
								<code className="bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-200 text-xs">
									200
								</code>
								<span>Success</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-green-50 text-green-700 px-2 py-0.5 rounded border border-green-200 text-xs">
									201
								</code>
								<span>Created (upload successful)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									400
								</code>
								<span>Bad request (invalid params)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									401
								</code>
								<span>Unauthorized (invalid API key)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									402
								</code>
								<span>Payment required (insufficient credits)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									403
								</code>
								<span>Forbidden (insufficient permissions)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									404
								</code>
								<span>Not found</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									413
								</code>
								<span>File too large (&gt;50MB)</span>
							</div>
							<div className="flex items-center gap-2">
								<code className="bg-red-50 text-red-700 px-2 py-0.5 rounded border border-red-200 text-xs">
									500
								</code>
								<span>Server error</span>
							</div>
						</div>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Error Response Format:
						</Typography>
						<CodeBlock>{`{
  "success": false,
  "error_code": "FILE_NOT_FOUND",
  "message": "Statement file not found"
}`}</CodeBlock>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Error Codes:
						</Typography>
						<div className="flex flex-wrap gap-2">
							{[
								'MISSING_FILE',
								'INVALID_FILE_TYPE',
								'INSUFFICIENT_CREDITS',
								'FILE_NOT_FOUND',
								'FILE_NOT_READY',
								'VALIDATION_ERROR',
								'S3_UPLOAD_FAILED',
								'CONFIG_NOT_FOUND',
								'NO_ACTIVE_CONFIG',
								'SETTINGS_NOT_FOUND',
							].map((code) => (
								<code
									key={code}
									className="bg-gray-100 px-2 py-0.5 rounded text-xs text-gray-700"
								>
									{code}
								</code>
							))}
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'rate-limits',
			title: 'Rate Limits',
			icon: Gauge,
			content: (
				<div className="space-y-4">
					<Typography variant="body2" className="mb-4">
						Rate limits are applied per tenant to ensure fair usage.
					</Typography>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
						<div className="bg-gray-50 rounded-lg p-4 text-center">
							<Typography variant="h3" className="text-purple-600">
								10
							</Typography>
							<Typography variant="body2" color="muted">
								uploads / minute
							</Typography>
						</div>
						<div className="bg-gray-50 rounded-lg p-4 text-center">
							<Typography variant="h3" className="text-purple-600">
								100
							</Typography>
							<Typography variant="body2" color="muted">
								status & results / minute
							</Typography>
						</div>
						<div className="bg-gray-50 rounded-lg p-4 text-center">
							<Typography variant="h3" className="text-purple-600">
								100
							</Typography>
							<Typography variant="body2" color="muted">
								list requests / minute
							</Typography>
						</div>
					</div>

					<div>
						<Typography variant="body2" weight="medium" className="mb-2">
							Rate limit headers included in responses:
						</Typography>
						<CodeBlock>{`X-RateLimit-Limit: 10
X-RateLimit-Remaining: 9
X-RateLimit-Reset: 1640606400`}</CodeBlock>
					</div>
				</div>
			),
		},
		{
			id: 'best-practices',
			title: 'Integration Best Practices',
			icon: Zap,
			content: (
				<div className="space-y-4">
					<div className="space-y-4">
						<div>
							<Typography variant="body2" weight="medium" className="mb-2">
								Polling Pattern:
							</Typography>
							<Typography variant="body2" color="muted" className="mb-2">
								Check status every 5 seconds. Timeout after 5 minutes (60
								attempts).
							</Typography>
							<CodeBlock>{`async function waitForCompletion(fileId) {
  for (let i = 0; i < 60; i++) {
    const res = await fetch(
      \`https://app.mightybot.ai/statement-analyzer/api/statement/status/\${fileId}\`,
      {
        headers: {
          'X-API-Key': apiKey,
          'X-Tenant-ID': tenantId,
        },
      }
    );
    const data = await res.json();
    if (data.status === 'completed') return data;
    if (data.status === 'failed')
      throw new Error(data.logs?.at(-1)?.message || 'Failed');
    await new Promise((r) => setTimeout(r, 5000));
  }
  throw new Error('Timeout');
}`}</CodeBlock>
						</div>

						<div>
							<Typography variant="body2" weight="medium" className="mb-2">
								Error Handling:
							</Typography>
							<Typography variant="body2" color="muted">
								Always check the <code>success</code> field in responses. Use{' '}
								<code>error_code</code> for programmatic handling and{' '}
								<code>message</code> for display to users.
							</Typography>
						</div>

						<div>
							<Typography variant="body2" weight="medium" className="mb-2">
								Retries:
							</Typography>
							<Typography variant="body2" color="muted">
								Use exponential backoff (1s, 2s, 4s) for transient 5xx failures.
								Do not retry 4xx errors.
							</Typography>
						</div>
					</div>
				</div>
			),
		},
		{
			id: 'webhooks',
			title: 'Webhooks & Support',
			icon: Headphones,
			content: (
				<div className="space-y-4">
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<Typography
							variant="body2"
							weight="medium"
							className="text-blue-800 mb-2"
						>
							Webhooks (Coming Soon)
						</Typography>
						<Typography variant="body2" className="text-blue-700">
							Real-time status updates will be available via webhooks. Subscribe
							to:
						</Typography>
						<ul className="list-disc list-inside text-sm text-blue-700 mt-2 ml-2">
							<li>
								<code>statement.processing.completed</code>
							</li>
							<li>
								<code>statement.processing.failed</code>
							</li>
						</ul>
					</div>

					<div className="border-t border-gray-200 pt-4">
						<Typography variant="body2" weight="medium" className="mb-3">
							Support:
						</Typography>
						<div className="space-y-2 text-sm text-gray-700">
							<div className="flex items-center gap-2">
								<Globe className="w-4 h-4 text-gray-400" />
								<span>
									Email:{' '}
									<a
										href="mailto:developers@mightybot.ai"
										className="text-purple-600 hover:underline"
									>
										developers@mightybot.ai
									</a>
								</span>
							</div>
						</div>
					</div>
				</div>
			),
		},
	];

	// ─── Navigation items ────────────────────────────────────────────────

	const navGroups = [
		{
			label: 'Getting Started',
			items: [
				{ id: 'quick-start', label: 'Quick Start' },
				{ id: 'authentication', label: 'Authentication' },
				{ id: 'complete-example', label: 'Complete Example' },
			],
		},
		{
			label: 'Statements',
			items: [
				{ id: 'statement-upload', label: 'Upload' },
				{ id: 'statement-status', label: 'Check Status' },
				{ id: 'statement-results', label: 'Get Results' },
				{ id: 'statement-list', label: 'Statement History' },
				{ id: 'recalculate', label: 'Recalculate' },
				{ id: 'processing-logs', label: 'Processing Logs' },
			],
		},
		{
			label: 'Configuration',
			items: [
				{ id: 'custom-rates', label: 'Custom Rates' },
				{ id: 'revenue-configs', label: 'Revenue Config' },
				{ id: 'enterprise-settings', label: 'Organization Settings' },
			],
		},
		{
			label: 'Administration',
			items: [{ id: 'tenant-management', label: 'Tenant Management' }],
		},
		{
			label: 'Reference',
			items: [
				{ id: 'errors', label: 'Errors' },
				{ id: 'rate-limits', label: 'Rate Limits' },
				{ id: 'best-practices', label: 'Best Practices' },
				{ id: 'webhooks', label: 'Webhooks & Support' },
				{ id: 'resources', label: 'Resources' },
			],
		},
	];

	// ─── Search filter ───────────────────────────────────────────────────

	const filteredSections = sections.filter(
		(section) =>
			!searchQuery ||
			section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
			section.id.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	// ─── Render ──────────────────────────────────────────────────────────

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Sticky Header */}
			<header className="sticky top-0 z-50 bg-white border-b border-gray-200">
				<div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-14">
					<div className="flex items-center gap-4">
						<Logo className="h-8 w-auto" />
						<div className="h-5 w-px bg-gray-200" />
						<Typography
							variant="body2"
							weight="medium"
							className="text-gray-700"
						>
							API Documentation
						</Typography>
					</div>
					<Badge variant="info">v1.0</Badge>
				</div>
			</header>

			<div className="max-w-7xl mx-auto flex">
				{/* Left Sidebar Navigation */}
				<aside className="hidden lg:block w-60 flex-shrink-0">
					<nav className="sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto py-6 pr-4 pl-6">
						{/* Search */}
						<div className="mb-5">
							<SearchInput
								placeholder="Search..."
								value={searchQuery}
								onChange={setSearchQuery}
								className="w-full"
							/>
						</div>

						{navGroups.map((group) => (
							<div key={group.label} className="mb-5">
								<Typography
									variant="body2"
									weight="medium"
									className="text-gray-400 uppercase text-[10px] tracking-wider mb-2 px-2"
								>
									{group.label}
								</Typography>
								<ul className="space-y-0.5">
									{group.items.map((item) => (
										<li key={item.id}>
											<button
												onClick={() => scrollToSection(item.id)}
												className={`w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors ${
													activeSection === item.id
														? 'bg-purple-50 text-purple-700 font-medium'
														: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
												}`}
											>
												{item.label}
											</button>
										</li>
									))}
								</ul>
							</div>
						))}
					</nav>
				</aside>

				{/* Main Content */}
				<main className="flex-1 min-w-0 py-6 px-6">
					{/* Mobile search - shown only on small screens */}
					<div className="lg:hidden mb-6">
						<SearchInput
							placeholder="Search API documentation..."
							value={searchQuery}
							onChange={setSearchQuery}
							className="w-full"
						/>
					</div>

					{/* Quick Start */}
					{(!searchQuery ||
						'quick start'.includes(searchQuery.toLowerCase())) && (
						<div
							id="quick-start"
							ref={(el) => {
								sectionRefs.current['quick-start'] = el;
							}}
						>
							<Card className="mb-6">
								<CardHeader>
									<Typography variant="h2">Quick Start</Typography>
								</CardHeader>
								<CardBody>
									<div className="space-y-4">
										<Typography variant="body1">
											The Statement Analysis API extracts fee data from merchant
											processor statements and calculates potential savings.
											Upload a PDF, wait for AI-powered extraction, and receive
											a detailed cost comparison showing exactly where your
											clients can save money.
										</Typography>
										<div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
											<Typography
												variant="body2"
												weight="medium"
												className="mb-3 text-purple-900"
											>
												Three API calls. That&apos;s it.
											</Typography>
											<ol className="space-y-2">
												<li className="flex items-center gap-2 text-sm text-purple-800">
													<span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs font-bold">
														1
													</span>
													Upload a statement PDF
												</li>
												<li className="flex items-center gap-2 text-sm text-purple-800">
													<span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs font-bold">
														2
													</span>
													Poll until processing completes (~60 seconds)
												</li>
												<li className="flex items-center gap-2 text-sm text-purple-800">
													<span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-200 text-purple-700 flex items-center justify-center text-xs font-bold">
														3
													</span>
													Retrieve your savings analysis
												</li>
											</ol>
											<Typography
												variant="body2"
												className="mt-3 text-purple-700"
											>
												<strong>Processing time:</strong> ~60 seconds (AI
												extraction)
											</Typography>
										</div>
									</div>
								</CardBody>
							</Card>
						</div>
					)}

					{/* API Sections */}
					<div className="space-y-6">
						{filteredSections.map((section) => {
							const Icon = section.icon;
							return (
								<div
									key={section.id}
									id={section.id}
									ref={(el) => {
										sectionRefs.current[section.id] = el;
									}}
								>
									<Card>
										<CardHeader>
											<div className="flex items-center gap-3">
												<div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
													<Icon className="w-5 h-5 text-purple-600" />
												</div>
												<Typography variant="h2">{section.title}</Typography>
											</div>
										</CardHeader>
										<CardBody>{section.content}</CardBody>
									</Card>
								</div>
							);
						})}
					</div>

					{/* Additional Resources */}
					{(!searchQuery ||
						'resources business types rate plan'.includes(
							searchQuery.toLowerCase(),
						)) && (
						<div
							id="resources"
							ref={(el) => {
								sectionRefs.current['resources'] = el;
							}}
						>
							<Card className="mt-6">
								<CardHeader>
									<Typography variant="h2">Additional Resources</Typography>
								</CardHeader>
								<CardBody>
									<div className="space-y-4">
										<div>
											<Typography
												variant="body2"
												weight="medium"
												className="mb-2"
											>
												Rate Plan Types:
											</Typography>
											<div className="overflow-x-auto">
												<table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
													<thead className="bg-gray-50">
														<tr>
															<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
																Type
															</th>
															<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
																Description
															</th>
															<th className="text-left px-3 py-2 font-medium text-gray-700 border-b">
																File Types
															</th>
														</tr>
													</thead>
													<tbody>
														<tr className="border-b border-gray-100">
															<td className="px-3 py-2">
																<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
																	IC_PLUS
																</code>
															</td>
															<td className="px-3 py-2 text-gray-700">
																Interchange Plus pricing. Recommended for most
																merchants.
															</td>
															<td className="px-3 py-2 text-gray-600">PDF</td>
														</tr>
														<tr className="border-b border-gray-100">
															<td className="px-3 py-2">
																<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
																	TIERED
																</code>
															</td>
															<td className="px-3 py-2 text-gray-700">
																Qualified/Mid-Qualified/Non-Qualified tiers.
																Traditional processors.
															</td>
															<td className="px-3 py-2 text-gray-600">PDF</td>
														</tr>
														<tr>
															<td className="px-3 py-2">
																<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
																	FLAT_TIERED
																</code>
															</td>
															<td className="px-3 py-2 text-gray-700">
																Flat rate with tiers (Stripe/Square style).
															</td>
															<td className="px-3 py-2 text-gray-600">
																Excel, CSV
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>

										<div className="border-t border-gray-200 pt-4">
											<Typography
												variant="body2"
												weight="medium"
												className="mb-2"
											>
												Business Types:
											</Typography>
											<div className="flex flex-wrap gap-1.5">
												{[
													'restaurants',
													'retail',
													'ecommerce',
													'card_not_present',
													'supermarkets_grocery_stores',
													'cafes_coffee_shops',
													'bars_pubs',
													'pharmacies',
													'convenience_stores',
													'gas_stations',
													'hotels',
													'salons_spas',
													'fitness_centers_gyms',
													'specialty_retail',
													'healthcare_facilities',
													'entertainment_venues',
													'educational_institutions',
												].map((type) => (
													<code
														key={type}
														className="bg-gray-100 px-2 py-0.5 rounded text-xs text-gray-700"
													>
														{type}
													</code>
												))}
											</div>
										</div>
									</div>
								</CardBody>
							</Card>
						</div>
					)}

					{/* Footer */}
					<div className="mt-8 pb-8 text-center text-xs text-gray-400">
						<Typography variant="body2" color="muted">
							Last Updated: January 2025 &middot; API Version: 1.0
						</Typography>
					</div>
				</main>
			</div>
		</div>
	);
};

export default ApiDocsPage;
